'use client';

import {
	Checkbox,
	ExpandableSection,
	IconBusinessCenter,
	IconExpand,
	IconResortActivities,
	IconWeddings,
	TextArea,
} from '../../components';
import { StayReason, StayReasonOption } from '../../types';
import { cn } from '@fshr/shared-react';
import React, { FC, PropsWithChildren, useState } from 'react';
import { useMediaQuery } from 'react-responsive';

const DEFAULT_SPECIAL_NOTE_MAX_LENGTH = 200;

type ReasonForStayProps = {
	t: (text: string, params?: (string | number)[]) => string;
	reasonForStay: StayReason[] | undefined;
	registerField: any;
	reasonsForStayComments: string | undefined;
	hideHeading?: boolean;
	expandable?: boolean;
	alwaysShowSpecialNote?: boolean;
	addReasonsForStayNamePrefix?: boolean;
	specialNoteMaxLength?: number;
	sanitizeSpecialNote?: (value: string) => string;
};

const ReasonsSectionWrapper: FC<
	PropsWithChildren<{ sectionTitle?: string; isLastSection?: boolean; isFirstSection?: boolean }>
> = ({ children, sectionTitle, isFirstSection }) => {
	const isLgOrHigher = useMediaQuery({ minWidth: '960px' });
	return isLgOrHigher ? (
		<>{children}</>
	) : (
		<ExpandableSection
			additionalClasses={cn('flex flex-col w-full border-b-[0px] border-t pt-[15px]', {
				'border-t-[0px]': isFirstSection,
			})}
			elementsContainerClassName="py-[0px]"
			title={sectionTitle ?? ''}
			CustomExpandIcon={({ isExpanded }) => (
				<IconExpand className={isExpanded ? 'rotate-180' : ''} />
			)}
		>
			<div className="flex flex-col w-full spacing-stack-xs pt-[15px]">{children}</div>
		</ExpandableSection>
	);
};

export function ReasonForStay(props: ReasonForStayProps) {
	const {
		t,
		reasonForStay,
		registerField,
		reasonsForStayComments,
		hideHeading = false,
		expandable = true,
		alwaysShowSpecialNote = false,
		addReasonsForStayNamePrefix,
		specialNoteMaxLength = DEFAULT_SPECIAL_NOTE_MAX_LENGTH,
		sanitizeSpecialNote,
	} = props;
	const leisure = reasonForStay?.filter(x => x.reasonType.id === 'leisure');
	const special_occasion = reasonForStay?.filter(x => x.reasonType.id === 'special_occasion');
	const bussiness = reasonForStay?.filter(x => x.reasonType.id === 'business');
	const reasonsForStay = [...(leisure ?? []), ...(special_occasion ?? []), ...(bussiness ?? [])];
	const [selected, setSelected] = useState<string[]>([]);
	const isLgOrHigher = useMediaQuery({ minWidth: '960px' });
	const commentsInitialValue = sanitizeSpecialNote
		? sanitizeSpecialNote(reasonsForStayComments ?? '').slice(0, specialNoteMaxLength)
		: (reasonsForStayComments ?? '');
	const commentsField = registerField({
		name: 'comments',
		initialValue: commentsInitialValue,
		options: {
			required: false,
		},
	});

	const handleChange = (value: boolean, option: StayReasonOption) => {
		if (value) {
			setSelected([...selected, option.value]);
		} else {
			setSelected(selected.filter(v => v !== option.value));
		}
	};

	const StayReasonsCheckbox = ({
		option,
		index,
		selected,
	}: {
		option: StayReasonOption;
		index: number;
		selected: string[];
	}) => {
		const isChecked = option.selected || selected.includes(option.value);
		const isLgOrHigher = useMediaQuery({ minWidth: '960px' });
		return (
			<Checkbox
				disabled={selected.length >= 3 && !isChecked}
				checked={isChecked}
				label={option.label}
				inputClassName="bg-transparent"
				key={index}
				options={{ checked: true, unchecked: false }}
				labelClassName={cn({
					'text-body3': isLgOrHigher,
					'text-body2': !isLgOrHigher,
				})}
				{...registerField({
					name: addReasonsForStayNamePrefix ? `reasonForStay.${option.label}` : option.label,
					initialValue: isChecked ? 'true' : '',
					type: 'checkbox',
					options: {
						required: false,
						onChange: (value: boolean) => {
							handleChange(value, option);
						},
					},
				})}
			/>
		);
	};

	const displayStayReasonOptions = (
		stayReasons: StayReason[] | undefined,
		sectionOptions?: {
			isFirstSection?: boolean;
			isLastSection?: boolean;
			sectionTitle?: string;
		}
	) => {
		const { isFirstSection, isLastSection, sectionTitle } = sectionOptions ?? {};
		return stayReasons?.map((stayReason, index) => {
			return (
				<ReasonsSectionWrapper
					key={index}
					sectionTitle={sectionTitle}
					isLastSection={isLastSection}
					isFirstSection={isFirstSection}
				>
					{stayReason?.options?.map((option, i) => (
						<StayReasonsCheckbox
							key={option.value}
							index={index}
							option={option}
							selected={selected}
						/>
					))}
				</ReasonsSectionWrapper>
			);
		});
	};

	const renderStayReasonOptions = (stayReasons: StayReason[] | undefined) => {
		return stayReasons?.map((stayReason, index) => (
			<React.Fragment key={index}>
				{stayReason?.options?.map((option, i) => (
					<StayReasonsCheckbox
						key={option.value}
						index={index}
						option={option}
						selected={selected}
					/>
				))}
			</React.Fragment>
		));
	};

	return (
		<>
			{!hideHeading && (
				<div className="flex flex-col lg:gap-100">
					<div className="flex">
						<h3 className="text-subtitle1">{t('Reason For Stay')}</h3>
					</div>
					<p className="text-body2">
						{t(
							'Celebrating a special occasion? We will gladly assist with preparing a special surprise for your companion or family member.'
						)}
					</p>
				</div>
			)}
			<div className={cn('flex flex-col w-full spacing-stack-xl')}>
				<div className="flex w-full">
					<div
						className={cn('flex flex-col lg:grid lg:grid-cols-3 w-full', {
							'spacing-stack-l': !isLgOrHigher,
							'gap-400': isLgOrHigher,
						})}
					>
						<div className="flex flex-col">
							{(isLgOrHigher || !expandable) && (
								<div className="flex items-center gap-100 spacing-heading-s">
									<IconResortActivities />
									<h2 className="text-subtitle2">{t('Leisure')}</h2>
								</div>
							)}
							<div className="flex flex-col gap-100">
								{expandable
									? displayStayReasonOptions(leisure, {
											sectionTitle: t('Leisure'),
											isFirstSection: true,
										})
									: renderStayReasonOptions(leisure)}
							</div>
						</div>
						<div className="flex flex-col">
							{(isLgOrHigher || !expandable) && (
								<div className="flex items-center gap-100 spacing-heading-s">
									<IconWeddings />
									<h2 className="text-subtitle2">{t('Celebration')}</h2>
								</div>
							)}
							<div className="flex flex-col gap-100">
								{expandable
									? displayStayReasonOptions(special_occasion, {
											sectionTitle: t('Celebration'),
										})
									: renderStayReasonOptions(special_occasion)}
							</div>
						</div>
						<div className="flex flex-col">
							{(isLgOrHigher || !expandable) && (
								<div className="flex items-center gap-100 spacing-heading-s">
									<IconBusinessCenter />
									<h2 className="text-subtitle2">{t('Business')}</h2>
								</div>
							)}
							<div className="flex flex-col gap-100">
								{expandable
									? displayStayReasonOptions(bussiness, {
											sectionTitle: t('Business'),
											isLastSection: true,
										})
									: renderStayReasonOptions(bussiness)}
							</div>
						</div>
					</div>
				</div>
				{(selected.length > 0 ||
					alwaysShowSpecialNote ||
					reasonsForStay?.some(opt => opt.options.some(o => o.selected))) && (
					<div className="flex w-full">
						<div className="flex-1 flex flex-col">
							<h2 className="text-subtitle1">{t('Special Notes')}</h2>

							<TextArea
								maxLength={specialNoteMaxLength}
								readOnly={false}
								wrapperClassName="pb-[0px] lg:pb-[0px]"
								className="text-body3 px-[10px] py-[16px] w-full h-[104px] resize-none"
								placeholder="Let us know of more details and we can arrange a special arrival for the occasion. "
								{...commentsField}
								{...(sanitizeSpecialNote
									? {
											onChange: event => {
												event.target.value = sanitizeSpecialNote(event.target.value);
												commentsField.onChange(event);
											},
										}
									: {})}
							/>
						</div>
					</div>
				)}
			</div>
		</>
	);
}
